import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { TrColumn } from '@shared/restaurant/models';

import { TrCellDirective } from '../tr-table/tr-cell.directive';
import { TrGroupHeaderDirective } from '../tr-table/tr-group-header.directive';

/**
 * Narrow-screen list layout for {@link TrTableComponent}.
 *
 * On a phone there is no room for columns, so each record is stacked into one
 * block and every field carries its own label — the layout the app had before the
 * shared table, which used to be produced by collapsing the `<table>` itself
 * (`thead { display: none }`, `tr`/`td { display: block }` below `xs`) with a
 * hand-written `d-xs-inline d-sm-none` label inside each cell.
 *
 * This is the same look built as a real layout instead: `<ul>`/`<li>` blocks and
 * flex rows, no table elements, nothing fighting `table-layout: fixed`, and the
 * labels come from the column model rather than being duplicated in every host's
 * markup.
 *
 * It renders nothing of its own: the host's projected `*trCell` templates and the
 * column list are handed over by the table shell, so a record shows exactly the
 * same cell content it would show in a column — and a column hidden or reordered
 * for this device keeps that state, because it is the same `TrColumn[]`.
 */
@Component({
  selector: 'tablesready-tr-list',
  templateUrl: './tr-list.component.html',
  styleUrls: ['./tr-list.component.scss'],
  standalone: false,
})
export class TrListComponent<T = unknown> implements AfterViewInit, OnChanges, OnDestroy {
  /** Records to render, in display order. */
  @Input() rows: T[] = [];
  /** Visible columns, in display order — pinning is meaningless here, order is not. */
  @Input() columns: TrColumn[] = [];
  /** Keys of those columns, passed straight to each cell template (see the table shell). */
  @Input() visibleKeys = new Set<string>();
  /** The host's projected cell templates, keyed as in the table shell. */
  @Input() cellTemplates = new Map<string, TrCellDirective>();
  /** The host's projected group-header template, when it groups its rows. */
  @Input() groupDir?: TrGroupHeaderDirective;
  /** Group key for a row; a heading is emitted above the first row of each group. */
  @Input() groupBy?: (row: T, index: number, rows: T[]) => string | null;

  @Input() dataKey = '_id';
  @Input() trackBy?: (index: number, row: T) => unknown;

  /** Height of the scroll viewport; mirrors the table's `scrollHeight`. */
  @Input() viewportHeight?: string;
  /** True when the host has rows beyond the current slice. */
  @Input() hasMore = false;
  @Input() emptyMessage = 'No records.';

  @Input() rowId?: (row: T) => string;
  @Input() rowClass?: (row: T) => string | null | undefined;
  @Input() rowHidden?: (row: T) => boolean;
  @Input() highlightedId: string | null = null;

  /** Emitted when the scroll nears the bottom and more rows should window in. */
  @Output() loadMore = new EventEmitter<void>();

  @ViewChild('viewport') private viewport?: ElementRef<HTMLElement>;

  private static readonly LOAD_MORE_THRESHOLD_PX = 300;
  private pendingLoad = false;
  private scrollEl?: HTMLElement;

  constructor(private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    // The component is created and destroyed as the breakpoint flips, so the
    // viewport always exists by the time this runs.
    this.scrollEl = this.viewport?.nativeElement;
    if (this.scrollEl) {
      this.zone.runOutsideAngular(() => {
        this.scrollEl?.addEventListener('scroll', this.onScroll, { passive: true });
      });
    }
  }

  /** A new slice of rows has arrived — allow the next windowing request. */
  ngOnChanges(): void {
    this.pendingLoad = false;
  }

  ngOnDestroy(): void {
    this.scrollEl?.removeEventListener('scroll', this.onScroll);
  }

  trackByColKey = (_: number, col: TrColumn): string => col.key;

  trackByRow = (index: number, row: T): unknown =>
    this.trackBy ? this.trackBy(index, row) : (row as Record<string, unknown>)?.[this.dataKey];

  /** Fallback field rendering when the host projects no template for a column. */
  fieldValue(row: T, field?: string): unknown {
    return field ? (row as Record<string, unknown>)?.[field] : '';
  }

  /** A field is labelled unless the column opts out (`listLabel: false`). */
  showLabel(col: TrColumn): boolean {
    return col.listLabel !== false && !!col.header;
  }

  rowIdFor(row: T): string | null {
    return this.rowId ? this.rowId(row) : null;
  }

  rowClassFor(row: T): string | null | undefined {
    return this.rowClass ? this.rowClass(row) : null;
  }

  isRowHidden(row: T): boolean {
    return this.rowHidden ? this.rowHidden(row) : false;
  }

  isHighlighted(row: T): boolean {
    return (
      this.highlightedId != null &&
      (row as Record<string, unknown>)?.[this.dataKey] === this.highlightedId
    );
  }

  // ----- group headings ----------------------------------------------------

  groupKeyAt(index: number): string | null {
    if (!this.groupBy || !this.rows[index]) {
      return null;
    }
    return this.groupBy(this.rows[index], index, this.rows);
  }

  /** True when row `index` begins a new group (or is the first row). */
  isGroupBoundary(index: number): boolean {
    if (!this.groupBy) {
      return false;
    }
    const current = this.groupKeyAt(index);
    if (current == null) {
      return false;
    }
    return index === 0 || this.groupKeyAt(index - 1) !== current;
  }

  private readonly onScroll = (): void => {
    if (!this.scrollEl || !this.hasMore || this.pendingLoad) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = this.scrollEl;
    if (scrollTop + clientHeight >= scrollHeight - TrListComponent.LOAD_MORE_THRESHOLD_PX) {
      this.pendingLoad = true;
      this.zone.run(() => this.loadMore.emit());
    }
  };
}
